(() => {
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

  class BonsaiParitySlider {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-bonsai-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-bonsai-dot]'));
      this.previousButton = root.querySelector('[data-bonsai-prev]');
      this.nextButton = root.querySelector('[data-bonsai-next]');
      this.pauseButton = root.querySelector('[data-bonsai-pause]');
      this.manualControls = root.querySelector('[data-bonsai-manual-controls]');
      this.swipeTarget = root.matches('.bonsai-parity-testimonials')
        ? root.querySelector('.bonsai-parity-testimonials__viewport')
        : null;
      this.enhancedControls = Array.from(root.querySelectorAll('[data-bonsai-enhanced-controls]'));
      this.manualControlsInitiallyHidden = false;
      this.autoplay = root.dataset.autoplay !== 'false';
      this.loop = root.dataset.loop === 'true';
      this.resetOnExit = root.dataset.resetOnExit !== 'false';
      this.pauseOnHover = root.dataset.pauseOnHover !== 'false';
      this.delay = Number.parseInt(root.dataset.autoplayDelay || '0', 10);
      this.resetDelay = Number.parseInt(root.dataset.resetDelay || '1000', 10);
      this.transitionDuration = Number.parseInt(root.dataset.transitionDuration || '500', 10);
      this.activeIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active')),
      );
      this.timer = null;
      this.resetTimer = null;
      this.slideHideTimer = null;
      this.transitionToken = 0;
      this.isIntersecting = false;
      this.isPausedByUser = false;
      this.isPointerOver = false;
      this.isPausedByFocus = false;
      this.autoplayWhileFocused = false;
      this.swipeStart = null;

      if (this.slides.length < 1) return;

      this.enhancedControls.forEach((controls) => {
        controls.hidden = false;
      });
      this.manualControlsInitiallyHidden = this.manualControls?.hidden || false;
      this.root.style.setProperty('--bonsai-slide-duration', `${this.transitionDuration}ms`);
      this.bindEvents();
      this.syncMotionPreference();
      this.show(this.activeIndex, { announce: false });
      this.observe();
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => {
        this.show(this.activeIndex - 1).then(() => this.restartAutoplay());
      });

      this.nextButton?.addEventListener('click', () => {
        this.show(this.activeIndex + 1).then(() => this.restartAutoplay());
      });

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.show(index).then(() => this.restartAutoplay());
        });
      });

      this.pauseButton?.addEventListener('click', () => {
        this.isPausedByUser = !this.isPausedByUser;
        this.pauseButton.setAttribute('aria-pressed', String(this.isPausedByUser));
        this.pauseButton.textContent = this.isPausedByUser
          ? this.pauseButton.dataset.playLabel
          : this.pauseButton.dataset.pauseLabel;

        if (this.isPausedByUser) {
          this.autoplayWhileFocused = false;
          this.stopAutoplay();
        } else {
          this.autoplayWhileFocused = true;
          this.startAutoplay();
        }
      });

      this.root.addEventListener('pointerdown', () => {
        this.isPausedByFocus = false;
        this.autoplayWhileFocused = false;
        this.startAutoplay();
      });

      this.swipeTarget?.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        if (event.pointerType === 'mouse') event.preventDefault();

        this.swipeStart = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
        };
        this.swipeTarget.classList.add('is-swiping');
        this.swipeTarget.setPointerCapture?.(event.pointerId);
      });

      this.swipeTarget?.addEventListener('pointerup', (event) => {
        if (!this.swipeStart || event.pointerId !== this.swipeStart.pointerId) return;

        const horizontalDistance = event.clientX - this.swipeStart.x;
        const verticalDistance = event.clientY - this.swipeStart.y;
        this.swipeStart = null;
        this.swipeTarget.classList.remove('is-swiping');
        this.swipeTarget.releasePointerCapture?.(event.pointerId);

        if (
          Math.abs(horizontalDistance) < 48 ||
          Math.abs(horizontalDistance) <= Math.abs(verticalDistance) * 1.2
        ) {
          return;
        }

        const direction = horizontalDistance < 0 ? 1 : -1;
        this.show(this.activeIndex + direction).then(() => this.restartAutoplay());
      });

      this.swipeTarget?.addEventListener('pointercancel', (event) => {
        if (event.pointerId !== this.swipeStart?.pointerId) return;
        this.swipeStart = null;
        this.swipeTarget.classList.remove('is-swiping');
      });

      this.swipeTarget?.addEventListener('lostpointercapture', () => {
        this.swipeStart = null;
        this.swipeTarget.classList.remove('is-swiping');
      });

      if (this.pauseOnHover) {
        this.root.addEventListener('pointerenter', () => {
          this.isPointerOver = true;
          this.stopAutoplay();
        });
        this.root.addEventListener('pointerleave', () => {
          this.isPointerOver = false;
          this.startAutoplay();
        });
      }

      this.root.addEventListener('focusin', (event) => {
        this.autoplayWhileFocused = false;
        this.isPausedByFocus = event.target instanceof HTMLElement && event.target.matches(':focus-visible');
        if (this.isPausedByFocus) this.stopAutoplay();
      });
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) {
          this.isPausedByFocus = false;
          this.autoplayWhileFocused = false;
          this.startAutoplay();
        }
      });

      this.root.addEventListener('keydown', (event) => {
        this.isPausedByFocus = true;
        this.autoplayWhileFocused = false;
        this.stopAutoplay();

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.activeIndex - 1).then(() => this.restartAutoplay());
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.activeIndex + 1).then(() => this.restartAutoplay());
        }
      });

      reducedMotion.addEventListener('change', () => this.syncMotionPreference());

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else if (this.isIntersecting) {
          this.startAutoplay();
        }
      });
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.isIntersecting = true;
        this.startAutoplay();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.isIntersecting = entry.isIntersecting && entry.intersectionRatio >= 0.25;

          if (this.isIntersecting) {
            window.clearTimeout(this.resetTimer);
            this.startAutoplay();
            return;
          }

          this.stopAutoplay();
          window.clearTimeout(this.resetTimer);
          if (!this.resetOnExit || reducedMotion.matches) return;
          this.resetTimer = window.setTimeout(() => {
            if (!this.isIntersecting) this.show(0, { announce: false });
          }, this.resetDelay);
        },
        { threshold: [0, 0.25, 1] },
      );

      this.observer.observe(this.root);
    }

    hydrateSlide(index) {
      const slide = this.slides[index];
      const template = slide?.querySelector('template[data-bonsai-slide-template]');
      if (template) template.replaceWith(template.content.cloneNode(true));
      return slide?.querySelector('img');
    }

    async ensureSlideReady(index) {
      const slide = this.slides[index];
      if (!slide) return false;

      const expectsImage = Boolean(
        slide.querySelector('img, template[data-bonsai-slide-template]'),
      );
      const image = this.hydrateSlide(index);
      if (!image) return !expectsImage;
      if (image.complete && image.naturalWidth > 0) return true;

      image.loading = 'eager';
      try {
        await image.decode();
      } catch {
        // Keep the current slide visible if an incoming asset fails to decode.
      }

      return image.complete && image.naturalWidth > 0;
    }

    preloadSlide(index) {
      const normalizedIndex = this.loop
        ? ((index % this.slides.length) + this.slides.length) % this.slides.length
        : Math.min(Math.max(index, 0), this.slides.length - 1);
      const image = this.hydrateSlide(normalizedIndex);
      if (!image) return;

      image.loading = 'eager';
      if (!image.complete) image.decode().catch(() => {});
    }

    async show(requestedIndex, options = {}) {
      const previousIndex = this.activeIndex;
      const nextIndex = this.loop
        ? ((requestedIndex % this.slides.length) + this.slides.length) % this.slides.length
        : Math.min(Math.max(requestedIndex, 0), this.slides.length - 1);
      const transitionToken = ++this.transitionToken;

      if (previousIndex !== nextIndex) {
        const slideIsReady = await this.ensureSlideReady(nextIndex);
        if (transitionToken !== this.transitionToken || !slideIsReady) return;
      }

      this.activeIndex = nextIndex;
      window.clearTimeout(this.slideHideTimer);
      this.slideHideTimer = null;

      this.slides.forEach((slide, index) => {
        const isActive = index === nextIndex;
        const isOutgoing = index === previousIndex && previousIndex !== nextIndex;
        slide.hidden = !isActive && (!isOutgoing || reducedMotion.matches);
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.toggleAttribute('inert', !isActive);
      });

      if (previousIndex !== nextIndex && !reducedMotion.matches) {
        const outgoingSlide = this.slides[previousIndex];
        this.slideHideTimer = window.setTimeout(() => {
          if (this.activeIndex !== previousIndex) outgoingSlide.hidden = true;
          this.slideHideTimer = null;
        }, this.transitionDuration);
      }

      this.dots.forEach((dot, index) => {
        const isActive = index === nextIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (this.previousButton) this.previousButton.disabled = !this.loop && nextIndex === 0;
      if (this.nextButton) {
        this.nextButton.disabled = !this.loop && nextIndex === this.slides.length - 1;
      }

      if (options.announce !== false) {
        const status = this.root.querySelector('[data-bonsai-slider-status]');
        if (status) status.textContent = `Slide ${nextIndex + 1} of ${this.slides.length}`;
      }

      if (this.slides.length > 1) this.preloadSlide(nextIndex + 1);
    }

    startAutoplay() {
      if (
        reducedMotion.matches ||
        !this.autoplay ||
        this.isPausedByUser ||
        this.isPointerOver ||
        (this.isPausedByFocus && !this.autoplayWhileFocused) ||
        document.hidden ||
        !this.isIntersecting ||
        !Number.isFinite(this.delay) ||
        this.delay <= 0 ||
        (!this.loop && this.activeIndex >= this.slides.length - 1)
      ) {
        return;
      }

      this.stopAutoplay();
      this.timer = window.setTimeout(async () => {
        await this.show(this.activeIndex + 1, { announce: false });
        this.startAutoplay();
      }, this.delay);
    }

    stopAutoplay() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    syncMotionPreference() {
      const motionIsReduced = reducedMotion.matches;

      if (this.manualControls) {
        this.manualControls.hidden = motionIsReduced ? false : this.manualControlsInitiallyHidden;
      }

      if (this.pauseButton) {
        this.pauseButton.hidden = motionIsReduced;
        this.pauseButton.disabled = motionIsReduced;
      }

      if (motionIsReduced) {
        this.stopAutoplay();
        window.clearTimeout(this.resetTimer);
        this.resetTimer = null;
        return;
      }

      if (this.isIntersecting) this.startAutoplay();
    }

    restartAutoplay() {
      this.stopAutoplay();
      if (this.isIntersecting) this.startAutoplay();
    }
  }

  class BonsaiParityParallax {
    constructor(scope = document) {
      this.items = [];
      this.visibleItems = new Set();
      this.frame = null;

      this.observe();
      this.bindEvents();
      this.add(scope);
    }

    bindEvents() {
      window.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
      window.addEventListener('resize', () => {
        this.measure();
        this.requestUpdate();
      });

      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) {
          window.cancelAnimationFrame(this.frame);
          this.frame = null;
          this.items.forEach((item) => {
            item.style.removeProperty('--bonsai-parallax-y');
            item.dataset.bonsaiCurrentOffset = '0';
          });
          return;
        }

        this.measure();
        this.requestUpdate();
      });
    }

    measure() {
      this.items = this.items.filter((item) => item.isConnected);
      this.items.forEach((item) => {
        const currentOffset = Number.parseFloat(item.dataset.bonsaiCurrentOffset || '0');
        const rect = item.getBoundingClientRect();
        item.dataset.bonsaiOriginTop = String(rect.top + window.scrollY - currentOffset);
      });
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.items.forEach((item) => this.visibleItems.add(item));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.visibleItems.add(entry.target);
            } else {
              this.visibleItems.delete(entry.target);
            }
          });
          this.requestUpdate();
        },
        { rootMargin: '35% 0px' },
      );

    }

    add(scope = document) {
      const newItems = Array.from(scope.querySelectorAll('[data-bonsai-parallax]')).filter(
        (item) => !this.items.includes(item),
      );

      if (newItems.length < 1) return;

      this.items.push(...newItems);
      newItems.forEach((item) => this.observer?.observe(item));
      this.measure();
      this.requestUpdate();
    }

    requestUpdate() {
      if (this.frame || reducedMotion.matches) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const viewportHeight = window.innerHeight;
      const candidates = this.visibleItems.size > 0 ? this.visibleItems : this.items;

      candidates.forEach((item) => {
        const originTop = Number.parseFloat(item.dataset.bonsaiOriginTop || '0');
        const speed = Number.parseFloat(item.dataset.parallaxSpeed || '-0.03');
        const max = Number.parseFloat(item.dataset.parallaxMax || '72');
        const viewportCenterOffset = viewportHeight * 0.5 - item.offsetHeight * 0.5;
        const scrolled = window.scrollY - originTop + viewportCenterOffset;
        const offset = Math.min(Math.max(scrolled * speed, -max), max);

        item.dataset.bonsaiCurrentOffset = String(offset);
        item.style.setProperty('--bonsai-parallax-y', `${offset.toFixed(3)}px`);
      });
    }
  }

  class BonsaiParityAccordion {
    constructor(root) {
      this.root = root;
      this.duration = Number.parseInt(root.dataset.transitionDuration || '350', 10);
      this.items = Array.from(root.querySelectorAll('details'));

      this.root.style.setProperty('--bonsai-accordion-duration', `${this.duration}ms`);
      this.bindEvents();
    }

    bindEvents() {
      this.items.forEach((item) => {
        const summary = item.querySelector(':scope > summary');
        if (!summary) return;

        summary.addEventListener('click', (event) => {
          event.preventDefault();

          if (item.open && item.dataset.bonsaiClosing !== 'true') {
            this.close(item);
            return;
          }

          this.items.forEach((otherItem) => {
            if (otherItem !== item && otherItem.open) this.close(otherItem);
          });
          this.open(item);
        });
      });
    }

    open(item) {
      const answer = item.querySelector('.bonsai-parity-faq__answer');
      if (!answer) {
        item.open = true;
        return;
      }

      item.dataset.bonsaiClosing = 'false';
      item.open = true;
      item.classList.add('is-animating');

      if (reducedMotion.matches) {
        this.finish(item, answer, true);
        return;
      }

      answer.style.height = '0px';
      answer.getBoundingClientRect();
      answer.style.height = `${answer.scrollHeight}px`;
      this.finishOnTransition(item, answer, true);
    }

    close(item) {
      const answer = item.querySelector('.bonsai-parity-faq__answer');
      if (!answer) {
        item.open = false;
        return;
      }

      item.dataset.bonsaiClosing = 'true';
      item.classList.add('is-animating');

      if (reducedMotion.matches) {
        this.finish(item, answer, false);
        return;
      }

      answer.style.height = `${answer.scrollHeight}px`;
      answer.getBoundingClientRect();
      answer.style.height = '0px';
      this.finishOnTransition(item, answer, false);
    }

    finishOnTransition(item, answer, open) {
      const fallback = window.setTimeout(() => this.finish(item, answer, open), this.duration + 80);
      answer.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName !== 'height') return;
          window.clearTimeout(fallback);
          this.finish(item, answer, open);
        },
        { once: true },
      );
    }

    finish(item, answer, open) {
      item.open = open;
      item.classList.remove('is-animating');
      delete item.dataset.bonsaiClosing;
      answer.style.removeProperty('height');
    }
  }

  class BonsaiStickyPurchase extends HTMLElement {
    connectedCallback() {
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.productInfo = this.closest('product-info');
      this.proxyButton = this.querySelector('[data-bonsai-sticky-button]');
      this.selection = this.querySelector('[data-bonsai-sticky-selection]');
      this.price = this.querySelector('[data-bonsai-sticky-price]');
      this.onScroll = this.syncVisibility.bind(this);
      this.onChange = () => requestAnimationFrame(() => this.sync());
      this.onBreakpointChange = () => {
        this.sync();
        this.syncVisibility();
      };

      if (!this.productInfo || !this.proxyButton) return;

      this.proxyButton.addEventListener('click', () => this.activate());
      this.productInfo.addEventListener('change', this.onChange);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.mobileQuery.addEventListener('change', this.onBreakpointChange);

      this.mutationObserver = new MutationObserver((records) => {
        if (records.every((record) => this.contains(record.target))) return;
        this.onChange();
      });
      this.mutationObserver.observe(this.productInfo, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'data-bonsai-size-selected'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      this.sync();
      this.syncVisibility();
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener('change', this.onChange);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.mobileQuery?.removeEventListener('change', this.onBreakpointChange);
      this.mutationObserver?.disconnect();
    }

    getSourceButton() {
      return this.productInfo?.querySelector('.product-form__submit');
    }

    activate() {
      const sourceButton = this.getSourceButton();
      if (!sourceButton) return;

      if (this.productInfo.dataset.bonsaiSizeSelected !== 'true') {
        const sizeGroup = this.productInfo.querySelector('.product-form__input--option-size');
        sizeGroup?.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'center',
        });
        window.setTimeout(() => {
          sizeGroup?.querySelector('input:not(.disabled)')?.focus();
        }, reducedMotion.matches ? 0 : 350);
        return;
      }

      if (!sourceButton.disabled && sourceButton.getAttribute('aria-disabled') !== 'true') sourceButton.click();
    }

    sync() {
      const sourceButton = this.getSourceButton();
      if (!sourceButton) return;

      const size = this.productInfo.querySelector('.product-form__input--option-size input:checked')?.value;
      const gender = this.productInfo.querySelector('.product-form__input--option-gender input:checked')?.value;
      const quantity = this.productInfo.querySelector('.quantity__input')?.value || '1';
      const visiblePrice = this.productInfo.querySelector('[id^="price-"] .price-item--regular')?.textContent?.trim();
      const requiresSize = this.productInfo.dataset.bonsaiSizeSelected !== 'true' || !size;
      const sourceLabel = sourceButton.querySelector('span')?.textContent?.trim() || 'Add to cart';

      if (this.selection) {
        this.selection.textContent = requiresSize ? 'Choose a size' : `${gender || ''} ${size} · Qty ${quantity}`.trim();
      }
      if (this.price && visiblePrice) this.price.textContent = visiblePrice;

      if (requiresSize) {
        this.proxyButton.disabled = false;
        this.proxyButton.textContent = 'Choose size';
      } else {
        const unavailable = sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true';
        this.proxyButton.disabled = unavailable;
        this.proxyButton.textContent = unavailable ? sourceLabel : 'Add to cart';
      }
    }

    syncVisibility() {
      const sourceButton = this.getSourceButton();
      if (!sourceButton || !this.mobileQuery.matches) {
        this.hidden = true;
        return;
      }

      const sourceRect = sourceButton.getBoundingClientRect();
      const productRect = this.productInfo.getBoundingClientRect();
      const infoRect = this.productInfo.querySelector('.product__info-wrapper')?.getBoundingClientRect();
      const visibleSourceHeight = Math.max(
        0,
        Math.min(sourceRect.bottom, window.innerHeight) - Math.max(sourceRect.top, 0),
      );
      // Keep the proxy available until the inline control exposes a complete
      // touch target. A few clipped pixels at the viewport edge are not usable.
      const sourceIsActionable = visibleSourceHeight >= Math.min(sourceRect.height, 44);
      const purchaseContextIsVisible = Boolean(
        infoRect && infoRect.top < window.innerHeight && infoRect.bottom > 0 && productRect.bottom > 0,
      );

      this.hidden = !purchaseContextIsVisible || sourceIsActionable;
    }
  }

  if (!customElements.get('bonsai-sticky-purchase')) {
    customElements.define('bonsai-sticky-purchase', BonsaiStickyPurchase);
  }

  const sliderInstances = new WeakSet();
  const accordionInstances = new WeakSet();
  const fixedBackgroundInstances = new WeakSet();
  let revealObserver;
  let parallax;

  const initializeReveals = (scope = document) => {
    const elements = Array.from(scope.querySelectorAll('[data-bonsai-reveal]')).filter(
      (element) => element.dataset.bonsaiRevealReady !== 'true',
    );
    if (elements.length < 1) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-revealed'));
      return;
    }

    revealObserver =
      revealObserver ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.2 },
      );

    elements.forEach((element) => {
      element.dataset.bonsaiRevealReady = 'true';
      element.classList.add('is-reveal-ready');
      revealObserver.observe(element);
    });
  };

  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;

    document.querySelectorAll('[data-bonsai-reveal]').forEach((element) => {
      revealObserver?.unobserve(element);
      element.dataset.bonsaiRevealReady = 'true';
      element.classList.add('is-revealed');
    });
  });

  const bindHeaderAnchors = (scope = document) => {
    scope.querySelectorAll('.bonsai-parity-header .menu-drawer__menu-item[href*="#"]').forEach((link) => {
      if (link.dataset.bonsaiAnchorReady === 'true') return;
      link.dataset.bonsaiAnchorReady = 'true';

      link.addEventListener('click', (event) => {
        const drawer = link.closest('header-drawer');
        const summary = drawer?.querySelector(':scope > details > summary');
        if (!drawer || !summary || typeof drawer.closeMenuDrawer !== 'function') return;

        drawer.closeMenuDrawer(event, summary);
        summary.setAttribute('aria-expanded', 'false');
      });
    });
  };

  const clickIdParamNames = new Set(['gclid', 'fbclid', 'msclkid', 'dclid']);

  const bindAttributionLinks = (scope = document) => {
    const currentParams = new URLSearchParams(window.location.search);
    const attributionParams = Array.from(currentParams.entries()).filter(([name, value]) => {
      const normalizedName = name.toLowerCase();
      return value && (normalizedName.startsWith('utm_') || clickIdParamNames.has(normalizedName));
    });

    if (attributionParams.length < 1) return;

    scope.querySelectorAll('[data-preserve-campaign-params]').forEach((link) => {
      if (link.dataset.bonsaiCampaignReady === 'true') return;

      const destination = new URL(link.href, window.location.origin);
      if (destination.origin !== window.location.origin) return;

      attributionParams.forEach(([name, value]) => destination.searchParams.set(name, value));
      link.href = destination.toString();
      link.dataset.bonsaiCampaignReady = 'true';
    });
  };

  const initializeFixedBackgrounds = (scope = document) => {
    scope.querySelectorAll('[data-bonsai-fixed-background]').forEach((container) => {
      if (fixedBackgroundInstances.has(container)) return;

      const image = container.querySelector('[data-bonsai-background-source] img');
      if (!image) return;

      const syncBackground = () => {
        if (!image.currentSrc || image.naturalWidth < 1) return;
        container.style.setProperty('--bonsai-fixed-background', `url("${image.currentSrc}")`);
        container.classList.add('is-background-ready');
      };

      fixedBackgroundInstances.add(container);
      image.addEventListener('load', syncBackground);
      if (image.complete) syncBackground();
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-bonsai-slider]').forEach((slider) => {
      if (sliderInstances.has(slider)) return;
      sliderInstances.add(slider);
      new BonsaiParitySlider(slider);
    });

    scope.querySelectorAll('[data-bonsai-accordion="single"]').forEach((accordion) => {
      if (accordionInstances.has(accordion)) return;
      accordionInstances.add(accordion);
      new BonsaiParityAccordion(accordion);
    });

    initializeReveals(scope);
    bindHeaderAnchors(scope);
    bindAttributionLinks(scope);
    initializeFixedBackgrounds(scope);
    parallax?.add(scope);
  };

  document.addEventListener('DOMContentLoaded', () => {
    parallax = new BonsaiParityParallax();
    initialize();
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-bonsai-slide]');
    const root = slide?.closest('[data-bonsai-slider]');
    if (!slide || !root) return;

    const slides = Array.from(root.querySelectorAll('[data-bonsai-slide]'));
    const index = slides.indexOf(slide);
    root.querySelector(`[data-bonsai-dot][data-slide-index="${index}"]`)?.click();
  });
})();
